import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ShieldCheck, CheckCircle2, XCircle, AlertTriangle, Loader2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { stringifyDetail } from '../lib/utils';
import { logger } from '../lib/logger';

const API_BASE = window.location.origin;

/** Map known backend error strings to i18n keys */
function translateBackendError(detail, t) {
    if (!detail) return '';
    const map = {
        'ALREADY REDEEMED': t('vouchers.alreadyRedeemed'),
        'EXPIRED': t('vouchers.resultExpired'),
        'VOIDED': t('vouchers.statusVoided'),
        'Invalid PIN': t('vouchers.invalidPin'),
        'Voucher not found': t('vouchers.voucherNotFound'),
        'Voucher is not active': t('vouchers.resultExpired'),
        'LOCATION_MISMATCH': t('vouchers.locationMismatch', 'This voucher belongs to a different location'),
    };
    return map[detail] || detail;
}

export default function VoucherRedeem() {
    const { t } = useTranslation();
    const [code, setCode] = useState('');
    const [pin, setPin] = useState('');
    const [cardId, setCardId] = useState('');
    const [step, setStep] = useState('card'); // card | code | pin | result
    const [redeeming, setRedeeming] = useState(false);
    const [result, setResult] = useState(null); // { status, message, data }

    const handleRedeem = async () => {
        if (code.length < 6 || pin.length !== 4) return;
        setRedeeming(true);
        setResult(null);
        try {
            const res = await fetch(`${API_BASE}/api/vouchers/redeem`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ code: code.toUpperCase().trim(), pin, card_id: cardId.trim() || undefined }),
            });

            let data = {};
            try {
                data = await res.json();
            } catch (parseErr) {
                logger.warn('[VoucherRedeem] non-JSON response from /api/vouchers/redeem', parseErr);
            }

            if (res.ok) {
                setResult({ status: 'success', message: t('vouchers.redeemSuccessMessage'), data });
            } else if (res.status === 400) {
                setResult({ status: 'error', message: translateBackendError(stringifyDetail(data.detail), t) || t('vouchers.invalidVoucherNumber') });
            } else if (res.status === 409) {
                setResult({ status: 'already_redeemed', message: translateBackendError(stringifyDetail(data.detail), t) || t('vouchers.alreadyRedeemed') });
            } else if (res.status === 410) {
                setResult({ status: 'expired', message: translateBackendError(stringifyDetail(data.detail), t) || t('vouchers.resultExpired') });
            } else if (res.status === 403) {
                const errDetail = stringifyDetail(data.detail) || '';
                if (errDetail === 'LOCATION_MISMATCH') {
                    setResult({ status: 'error', message: translateBackendError(errDetail, t) });
                } else {
                    setResult({ status: 'error', message: t('vouchers.invalidPin') });
                }
            } else if (res.status === 404) {
                setResult({ status: 'error', message: translateBackendError(stringifyDetail(data.detail), t) || t('vouchers.voucherNotFound') });
            } else {
                setResult({ status: 'error', message: translateBackendError(stringifyDetail(data.detail), t) || t('vouchers.somethingWentWrong') });
            }
            setStep('result');
        } catch (e) {
            setResult({ status: 'error', message: t('vouchers.connectionErrorRetry') });
            setStep('result');
        } finally {
            setRedeeming(false);
        }
    };

    const reset = () => {
        setCode('');
        setPin('');
        setCardId('');
        setStep('card');
        setResult(null);
    };

    // === CARD ID ENTRY ===
    if (step === 'card') {
        return (
            <div className="min-h-screen bg-slate-950 flex items-center justify-center p-4" data-testid="redeem-card-screen">
                <div className="w-full max-w-xs space-y-6 text-center">
                    <div className="space-y-2">
                        <ShieldCheck className="w-10 h-10 text-emerald-500 mx-auto" />
                        <h1 className="text-lg font-semibold text-white">{t('vouchers.redeemVoucher')}</h1>
                        <p className="text-sm text-slate-400">{t('vouchers.enterCardId', 'Enter your card ID')}</p>
                    </div>

                    <Input
                        data-testid="redeem-card-input"
                        value={cardId}
                        onChange={(e) => setCardId(e.target.value.toUpperCase())}
                        onKeyDown={(e) => e.key === 'Enter' && cardId.trim().length >= 3 && setStep('code')}
                        placeholder="DEL001"
                        className="text-center text-2xl tracking-widest bg-slate-900 border-slate-700 text-white h-14 font-mono"
                        autoFocus
                        maxLength={20}
                    />

                    <Button
                        data-testid="redeem-card-next"
                        onClick={() => setStep('code')}
                        disabled={cardId.trim().length < 3}
                        className="w-full h-12 bg-emerald-600 hover:bg-emerald-700"
                    >
                        {t('common.next')}
                    </Button>
                </div>
            </div>
        );
    }

    // === CODE ENTRY ===
    if (step === 'code') {
        return (
            <div className="min-h-screen bg-slate-950 flex items-center justify-center p-4" data-testid="redeem-code-screen">
                <div className="w-full max-w-xs space-y-6 text-center">
                    <div className="space-y-2">
                        <ShieldCheck className="w-10 h-10 text-emerald-500 mx-auto" />
                        <h1 className="text-lg font-semibold text-white">{t('vouchers.redeemVoucher')}</h1>
                        <p className="text-sm text-slate-400">{t('vouchers.enterVoucherCode')}</p>
                    </div>

                    <Input
                        data-testid="redeem-code-input"
                        value={code}
                        onChange={(e) => setCode(e.target.value.toUpperCase())}
                        onKeyDown={(e) => e.key === 'Enter' && code.length >= 6 && setStep('pin')}
                        placeholder="RV-A7K3M9Q"
                        className="text-center text-2xl tracking-widest bg-slate-900 border-slate-700 text-white h-14 font-mono"
                        autoFocus
                        maxLength={12}
                    />

                    <Button
                        data-testid="redeem-code-next"
                        onClick={() => setStep('pin')}
                        disabled={code.length < 6}
                        className="w-full h-12 bg-emerald-600 hover:bg-emerald-700"
                    >
                        {t('common.next')}
                    </Button>

                    <button
                        onClick={() => { setStep('card'); setCode(''); }}
                        className="text-xs text-slate-500 hover:text-slate-400"
                        data-testid="redeem-code-back"
                    >
                        {t('common.back')}
                    </button>
                </div>
            </div>
        );
    }

    // === PIN ENTRY ===
    if (step === 'pin') {
        return (
            <div className="min-h-screen bg-slate-950 flex items-center justify-center p-4" data-testid="redeem-pin-screen">
                <div className="w-full max-w-xs space-y-6 text-center">
                    <div className="space-y-2">
                        <ShieldCheck className="w-10 h-10 text-emerald-500 mx-auto" />
                        <h1 className="text-lg font-semibold text-white">{t('vouchers.staffPin')}</h1>
                        <p className="text-sm text-slate-400">{t('vouchers.enterPinToConfirm')}</p>
                        <p className="text-xs text-slate-500 font-mono">{code}</p>
                    </div>

                    <Input
                        data-testid="redeem-pin-input"
                        type="password"
                        inputMode="numeric"
                        maxLength={4}
                        value={pin}
                        onChange={(e) => setPin(e.target.value.replace(/\D/g, '').slice(0, 4))}
                        onKeyDown={(e) => e.key === 'Enter' && pin.length === 4 && handleRedeem()}
                        placeholder="____"
                        className="text-center text-2xl tracking-[0.5em] bg-slate-900 border-slate-700 text-white h-14"
                        autoFocus
                    />

                    <Button
                        data-testid="redeem-confirm-btn"
                        onClick={handleRedeem}
                        disabled={pin.length !== 4 || redeeming}
                        className="w-full h-12 bg-emerald-600 hover:bg-emerald-700"
                    >
                        {redeeming ? <Loader2 className="w-4 h-4 animate-spin" /> : t('vouchers.redeem')}
                    </Button>

                    <button
                        onClick={() => { setStep('code'); setPin(''); }}
                        className="text-xs text-slate-500 hover:text-slate-400"
                    >
                        {t('common.back')}
                    </button>
                </div>
            </div>
        );
    }

    // === RESULT ===
    const statusConfig = {
        success: { icon: CheckCircle2, color: 'text-emerald-400', bg: 'bg-emerald-600/20' },
        already_redeemed: { icon: XCircle, color: 'text-amber-400', bg: 'bg-amber-600/20' },
        expired: { icon: AlertTriangle, color: 'text-red-400', bg: 'bg-red-600/20' },
        error: { icon: XCircle, color: 'text-red-400', bg: 'bg-red-600/20' },
    };

    const cfg = statusConfig[result?.status] || statusConfig.error;
    const Icon = cfg.icon;

    return (
        <div className="min-h-screen bg-slate-950 flex items-center justify-center p-4" data-testid="redeem-result-screen">
            <div className="w-full max-w-xs space-y-6 text-center">
                <div className={`w-16 h-16 rounded-full ${cfg.bg} flex items-center justify-center mx-auto`}>
                    <Icon className={`w-8 h-8 ${cfg.color}`} />
                </div>

                <div className="space-y-2">
                    <h2 className="text-xl font-bold text-white">
                        {result?.status === 'success' ? t('vouchers.resultRedeemed') :
                         result?.status === 'already_redeemed' ? t('vouchers.resultAlreadyRedeemed') :
                         result?.status === 'expired' ? t('vouchers.resultExpired') : t('vouchers.resultError')}
                    </h2>
                    <p className="text-sm text-slate-400">{result?.message}</p>
                </div>

                {result?.status === 'success' && result?.data && (
                    <div className="bg-slate-900 rounded-lg p-4 space-y-2 text-sm">
                        <div className="flex justify-between text-slate-400">
                            <span>{t('vouchers.code')}</span>
                            <span className="text-white font-mono">{result.data.code}</span>
                        </div>
                        <div className="flex justify-between text-slate-400">
                            <span>{t('vouchers.value')}</span>
                            <span className="text-emerald-400 font-semibold">${result.data.value}</span>
                        </div>
                        <div className="flex justify-between text-slate-400">
                            <span>{t('vouchers.redeemedBy')}</span>
                            <span className="text-white">{result.data.redeemed_by}</span>
                        </div>
                    </div>
                )}

                <Button
                    data-testid="redeem-another-btn"
                    onClick={reset}
                    variant="outline"
                    className="w-full border-slate-700 text-white hover:bg-slate-800"
                >
                    {t('vouchers.redeemAnother')}
                </Button>
            </div>
        </div>
    );
}
